<template>
    <Card>
        <template #header>
            <div class="pl-5 pt-4 font-medium text-[16px]">
                Atidėjimas ir priminimai
            </div>
        </template>
        <template #content>
            <div class="flex flex-col gap-4">
                <div class="flex flex-col gap-2 w-full max-w-[800px]">
                    <div class="flex flex-row gap-2">
                        <Select v-model="reminderType"
                                :options="reminderTypes"
                                optionLabel="name"
                                placeholder="Priminimo tipas"
                                class="w-full"
                                size="small"
                        />
                        <DatePicker id="datepicker-24h"
                                    v-model="reminderDatetime"
                                    showTime
                                    hourFormat="24"
                                    fluid
                                    class="w-full"
                                    dateFormat="yy-mm-dd"
                                    placeholder="Priminimo data ir laikas"
                                    size="small"
                        />
                    </div>
                    <Textarea placeholder="Aprašymas (nebūtinas)" v-model="reminderBody" class="w-full" size="small" autoResize />

                    <div class="flex flex-row gap-2">
                        <Button label="Išsaugoti priminimą" size="small" @click="saveReminder" :loading="reminderSaving" class="self-start" />
                        <Button v-if="reminderId" label="Atšaukti redagavimą" size="small" @click="resetReminderForm" class="self-start" severity="secondary" />
                    </div>
                </div>

                <div class="bg-gray-100 rounded">
                    <table class="table-auto w-full">
                        <thead>
                            <tr>
                                <th class="text-left px-4 py-2">Aprašymas</th>
                                <th class="text-left px-4 py-2">Priminimo tipas</th>
                                <th class="text-left px-4 py-2">Priminimo laikas</th>
                                <th class="text-left px-4 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="reminder in reminders" :key="reminder.id">
                                <td class="text-left px-4 py-2">
                                    {{ reminder.body }}
                                </td>
                                <td class="text-left px-4 py-2">
                                    {{ reminder.type }}
                                </td>
                                <td class="text-left px-4 py-2">
                                    {{ reminder.datetime }}
                                </td>
                                <td class="text-left px-4 py-2">
                                    <div class="flex flex-row gap-2">
                                        <Button size="small" icon="pi pi-pen-to-square" @click="editReminder(reminder)"></Button>
                                        <Button size="small" icon="pi pi-trash" severity="danger" @click="deleteReminder(reminder.id)"></Button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </Card>
</template>

<script setup lang="ts">
    // System imports
    import { ref, onMounted, computed, watch } from 'vue';
    import { apiRequest } from '@/services/axios.ts'
    import { format } from 'date-fns';

    // PrimeVue components imports
    import { useConfirm } from 'primevue/useconfirm'
    import Textarea from 'primevue/textarea';
    import Card from 'primevue/card'
    import Button from 'primevue/button';
    import Select from 'primevue/select';
    import DatePicker from 'primevue/datepicker';

    // Refs and variables
    const confirm = useConfirm();
    const reminders = ref<any>([]);
    const reminderTypes = ref<any>([]);
    const reminderId = ref<number>(0);
    const reminderType = ref<any>({
        'code': '',
        'name': ''
    });
    const reminderBody = ref();
    const reminderDatetime = ref();
    const reminderSaving = ref<boolean>(false);

    // Emits
    const emit = defineEmits<{
        (e: 'draft-changed', value: boolean): void
    }>();

    // Props
    let props = defineProps<{
        contact: any
    }>();

    const hasDraft = computed(() => {
        const reminderTypeValue = reminderType.value;
        const hasType =
            reminderTypeValue &&
            (
                (typeof reminderTypeValue === 'string' && reminderTypeValue.trim() !== '') ||
                (typeof reminderTypeValue === 'object' &&
                    ((reminderTypeValue.code ?? '') !== '' || (reminderTypeValue.name ?? '') !== ''))
            );
        const hasBody = typeof reminderBody.value === 'string' ? reminderBody.value.trim() !== '' : false;
        const hasDatetime = Boolean(reminderDatetime.value);

        return hasType || hasBody || hasDatetime || reminderId.value > 0;
    });

    // Hooks
    onMounted(() => {
        loadReminders();
    });

    watch(
        hasDraft,
        (value) => {
            emit('draft-changed', value);
        },
        { immediate: true }
    );

    // Methods
    async function saveReminder() {
        let datetime = format(reminderDatetime.value, 'yyyy-MM-dd HH:mm') + ':00';

        await apiRequest(
            'post',
            '/contacts/' + props.contact.id + '/reminders',
            {
                id: reminderId.value,
                type: reminderType.value.code,
                datetime: datetime,
                body: reminderBody.value
            }
        );

        resetReminderForm();

        await loadReminders()
    }

    async function loadReminders() {
        const response = await apiRequest(
            'get',
            '/contacts/' + props.contact.id + '/reminders'
        );

        reminders.value = response.data.data.reminders;
        reminderTypes.value = response.data.data.available_types;
    }

    function editReminder(reminder: any) {
        reminderType.value = { code: reminder.type_key, name: reminder.type };
        reminderBody.value = reminder.body;
        reminderDatetime.value = new Date(reminder.unformatted_datetime);
        reminderId.value = reminder.id;
    }

    function resetReminderForm() {
        reminderType.value = '';
        reminderBody.value = '';
        reminderDatetime.value = '';
        reminderId.value = 0;
    }

    async function deleteReminder(reminderId: number) {
        confirm.require({
            message: 'Ar tikrai norite ištrinti šį priminimą?',
            header: 'Patvirtinimas',
            icon: 'pi pi-exclamation-triangle',
            rejectProps: {
                label: 'Ne',
                severity: 'secondary',
                outlined: true,
            },
            acceptProps: {
                label: 'Taip'
            },
            accept: async () => {
                await apiRequest(
                    'delete',
                    '/contacts/' + props.contact.id + '/reminders/' + reminderId
                );

                await loadReminders();
            }
        });
    }
</script>
